import { create } from 'zustand'
import api, { clearTokens, setTokens } from '../api/axios'
import { translations } from '../configs/translations'
import { ClientApi } from '../services/clientApi'
import { NuiCallbacks } from '../utils/ClientEvents'

export interface State {
    language: keyof typeof translations
    userId: number | null
    selectedVehicleGameId: number | null
    userName: string | null
    isAuth: boolean
    isLoading: boolean
    isInitialized: boolean
    globalError: string | null
    debugMode: boolean
    debugLogs: Array<{
        timestamp: number
        type: 'request' | 'response' | 'error'
        data: any
    }>
    raceFramesVisible: boolean
    setRaceFramesVisible: (visible: boolean) => void
    setDebugMode: (enabled: boolean) => void
    addDebugLog: (type: 'request' | 'response' | 'error', data: any) => void
    clearDebugLogs: () => void
    setGlobalError: (error: string | null) => void
    login: (email: string, password: string, playerId: number) => Promise<void>
    logout: () => Promise<void>
    checkAuth: () => Promise<void>
    register: (username: string, email: string, password: string, playerId: number, ip: string) => Promise<void>
    setSelectedVehicleGameId: (vehicleGameId: number | null) => void
}

export const useStore = create<State>((set) => ({
    userId: null,
    userName: null,
    selectedVehicleGameId: null,
    gameId: null,
    isAuth: false,
    isLoading: true,
    isInitialized: false,
    globalError: null,
    debugMode: false,
    debugLogs: [],
    raceFramesVisible: false,
    setRaceFramesVisible: (visible) => set({ raceFramesVisible: visible }),
    language: 'ru' as keyof typeof translations,
    setLanguage: (language: keyof typeof translations) => set({ language }),
    setGlobalError: (error) => set({ globalError: error }),
    setDebugMode: (enabled) => set({ debugMode: enabled }),
    addDebugLog: (type, data) => set((state) => ({
        debugLogs: [{
            timestamp: Date.now(),
            type,
            data
        }, ...state.debugLogs].slice(0, 100) // Храним последние 100 логов
    })),
    clearDebugLogs: () => set({ debugLogs: [] }),
    login: async (email: string, password: string, playerId: number) => {
        try {
            set({ globalError: null })
            const response = await api.post('/api/v1/auth/authenticate', { email, password, playerId })

            const { accessToken, userId, userName, refreshToken } = response.data
            if (!accessToken) {
                throw new Error('Server response missing token')
            }

            setTokens(accessToken, refreshToken)
            localStorage.setItem('userId', userId)
            try {
                console.log('Saving JWT to client', { accessToken: response.data.accessToken, refreshToken: response.data.refreshToken })

                ClientApi.send(NuiCallbacks.SAVE_JWT, { accessToken, refreshToken })
            } catch (_) {
                console.log('Failed to save JWT on client', _)
            }
            set({ userId, isAuth: true, userName })
        } catch (error) {
            set({ globalError: error instanceof Error ? error.message : 'Ошибка при входе в систему' })
            throw error
        }
    },

    register: async (username: string, email: string, password: string, playerId: number, ip: string) => {
        try {
            set({ globalError: null })
            const response = await api.post('/api/v1/auth/register',
                { username, email, password, playerId, ip })

            const { accessToken, userId, refreshToken } = response.data
            if (!accessToken) {
                throw new Error('Server response missing token')
            }

            setTokens(accessToken, refreshToken)
            try {
                ClientApi.send(NuiCallbacks.SAVE_JWT, { accessToken, refreshToken })
            } catch (_) {
                console.log('Failed to save JWT on client')
            }
            localStorage.setItem('userId', String(userId))
            set({ userId, isAuth: true })
        } catch (error) {
            set({ globalError: error instanceof Error ? error.message : 'Ошибка при регистрации' })
            throw error
        }
    },

    logout: async () => {
        try {
            await api.post('/api/v1/auth/logout')
            clearTokens()
            localStorage.removeItem('userId')
            set({ userId: null, isAuth: false })
        } catch (error) {
            clearTokens()
            localStorage.removeItem('userId')
            set({ userId: null, isAuth: false })
        }
    },

    checkAuth: async () => {
        const accessToken = localStorage.getItem('accessToken')
        const userId = localStorage.getItem('userId')

        console.log('Checking auth:', {
            hasAccessToken: !!accessToken,
            hasUserId: !!userId
        })

        if (!accessToken || !userId) {
            clearTokens()
            set({ isLoading: false, isInitialized: true })
            return
        }

        // Просто проверяем наличие токена и устанавливаем состояние
        set({
            userId: Number(userId),
            isAuth: true,
            isInitialized: true,
            isLoading: false
        })
    },

    setSelectedVehicleGameId: (gameId: number | null) => {
        if (gameId === null) {
            localStorage.removeItem('selectedVehicleGameId')
        } else {
            localStorage.setItem('selectedVehicleGameId', gameId.toString())
        }
        set({ selectedVehicleGameId: gameId })
    },





})) 